import { useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { pageVariants, reducedPageVariants, springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useHaptic } from '../../hooks/useHaptic';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import LiquidProgressBar from '../../components/ui/LiquidProgressBar';
import ChildNameStep from './ChildNameStep';
import ChildAgeStep from './ChildAgeStep';
import ChildInterestsStep from './ChildInterestsStep';
import ChildMeetBuddyStep from './ChildMeetBuddyStep';

export default function ChildSetupFlow() {
  const { isAuthenticated } = useAuthStore();
  const { childSetupStep, setChildSetupStep } = useOnboardingStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();
  const navigate = useNavigate();

  const variants = reduced ? reducedPageVariants : pageVariants;

  // Reset to step 0 on mount
  useEffect(() => {
    setChildSetupStep(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  const advance = useCallback((next: 0 | 1 | 2 | 3) => {
    play('navigate');
    haptic.tap();
    setChildSetupStep(next);
  }, [play, haptic, setChildSetupStep]);

  const goBack = useCallback(() => {
    if (childSetupStep > 0) {
      const prev = (childSetupStep - 1) as 0 | 1 | 2 | 3;
      play('navigate');
      haptic.tap();
      setChildSetupStep(prev);
    }
  }, [childSetupStep, play, haptic, setChildSetupStep]);

  const progressPercent = ((childSetupStep + 1) / 4) * 100;

  const screens = [
    <ChildNameStep key="name" onNext={() => advance(1)} />,
    <ChildAgeStep key="age" onNext={() => advance(2)} onBack={goBack} />,
    <ChildInterestsStep key="interests" onNext={() => advance(3)} onBack={goBack} />,
    <ChildMeetBuddyStep key="buddy" onFinish={() => navigate('/parent')} />,
  ];

  return (
    <div className="min-h-screen bg-kid-bg texture-paper-grain overflow-hidden relative">
      {/* LiquidProgressBar with glow */}
      <div className="fixed top-0 left-0 right-0 z-50 px-0">
        <LiquidProgressBar
          percentage={progressPercent}
          color="var(--kid-ocean)"
          height="h-2"
          glow
        />
      </div>

      {/* Back button (steps 1-2 only) */}
      {childSetupStep > 0 && childSetupStep < 3 && (
        <button
          onClick={goBack}
          className="fixed top-6 left-4 z-50 text-kid-text-soft hover:text-kid-text p-2 rounded-kid touch-kid"
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={childSetupStep}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={reduced ? { duration: 0.2 } : springs.pageTurn}
        >
          {screens[childSetupStep]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
